import React from 'react';
import PropTypes from 'prop-types';
import { keyframes } from '@emotion/core';
import styledComponent from '../../utils/styledComponent';
import theme from '../../theme';

// One shimmering rectangle, which is the whole vocabulary a skeleton needs. Every
// skeleton on a page is built from these rather than carrying its own gradient and
// keyframes — three copies had already drifted apart before this existed.
//
// What this deliberately does NOT do is describe a page's layout. A skeleton only
// prevents a jump if it reserves the same box its real counterpart will fill, and
// the only reliable way to guarantee that is to define the box once and hand it to
// both. So each skeleton lives beside the component it stands in for and shares
// that component's own chrome; this atom supplies the fill and nothing else.
const sweep = keyframes({
  '0%': { backgroundPosition: '-300px 0' },
  '100%': { backgroundPosition: '300px 0' },
});

const StyledSkeleton = styledComponent('div', {
  backgroundImage: `linear-gradient(90deg, ${theme.colors.skeleton} 25%, ${theme.colors.skeletonSheen} 50%, ${theme.colors.skeleton} 75%)`,
  backgroundSize: '600px 100%',
  animation: `${sweep} 1.3s infinite linear`,

  '&.sk-soft': {
    backgroundImage: `linear-gradient(90deg, ${theme.colors.skeletonSoft} 25%, ${theme.colors.skeletonSoftSheen} 50%, ${theme.colors.skeletonSoft} 75%)`,
  },
  // A loop that never settles is the case this media query exists for.
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
});

export default function SkeletonBlock({ width, height, radius, tone }) {
  return (
    <StyledSkeleton
      className={tone === 'soft' ? 'sk-soft' : undefined}
      style={{ width, height, borderRadius: radius }}
      aria-hidden
    />
  );
}

SkeletonBlock.propTypes = {
  // Numbers are pixels; strings pass through, so '70%' and 'auto' both work.
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  radius: PropTypes.string,
  // 'soft' for large areas, which read as heavy at the default weight.
  tone: PropTypes.oneOf(['default', 'soft']),
};

SkeletonBlock.defaultProps = {
  width: '100%',
  height: 12,
  radius: theme.radii.badge,
  tone: 'default',
};
